import {
  WorkflowInstanceApprovalDto,
  WorkflowInstanceDetailDto,
  WorkflowInstanceEventDto,
  WorkflowInstanceTaskDto,
} from "../../types/workflowDto";

/** One row of the Run screen's History card (Epic 80430 QA item 12, 2026-10-21). */
export interface WorkflowRunHistoryEntry {
  key: string;
  action: string;
  /** "Supervisor → HR review" - the step the row is about, when it has one. */
  step: string | null;
  /** Who did it. Null for the engine's own hops, which the card shows as "System". */
  person: string | null;
  /** Test runs: who the row would have been for ("for Supervisor (Jane Doe)"). */
  intendedFor: string | null;
  /** Eastern, as stored - "2026-10-21T14:15:00". */
  at: string;
  reason: string | null;
}

export const HISTORY_SYSTEM_ACTOR = "System";

const EVENT_ACTIONS: Record<string, string> = {
  started: "Case started",
  advanced: "Step completed",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Case closed",
  completed: "Case completed",
  discarded: "Test run discarded",
  paused: "Paused",
  resumed: "Resumed",
  webservice: "Web service step",
  email: "Email step",
  taskreassigned: "Task reassigned",
  deadlettered: "Step failed",
};

const DECISION_ACTIONS: Record<string, string> = {
  accept: "Approved",
  reject: "Rejected",
  cancel: "Approval cancelled",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "2026-10-21T14:15:00" -> "Oct 21, 2026 2:15 PM ET". Read as written: Portal timestamps are
 * already Eastern, so they are never put through a time-zone-aware Date (see toStampTime).
 */
export const formatHistoryTime = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day, hour, minute] = match;
  const hours = Number(hour);
  const clock = `${hours % 12 === 0 ? 12 : hours % 12}:${minute} ${hours < 12 ? "AM" : "PM"}`;
  return `${MONTHS[Number(month) - 1]} ${Number(day)}, ${year} ${clock} ET`;
};

const intendedFor = (row: { intendedLabel?: string | null; intendedName?: string | null }) => {
  const label = row.intendedLabel?.trim();
  const name = row.intendedName?.trim();
  if (label && name) return `${label} (${name})`;
  return label || name || null;
};

const stepOf = (event: WorkflowInstanceEventDto): string | null => {
  if (event.eventType === "started") return event.toStageName;
  if (event.fromStageName && event.toStageName && event.fromStageName !== event.toStageName)
    return `${event.fromStageName} → ${event.toStageName}`;
  return event.fromStageName ?? event.toStageName;
};

const fromEvent = (event: WorkflowInstanceEventDto): WorkflowRunHistoryEntry => {
  const type = event.eventType ?? "";
  return {
    key: `event-${event.id}`,
    action: EVENT_ACTIONS[type] ?? (type ? type.charAt(0).toUpperCase() + type.slice(1) : "Step"),
    step: stepOf(event),
    person: event.createdByName?.trim() || null,
    intendedFor: null,
    at: event.createdOn,
    reason: event.userMemo?.trim() || null,
  };
};

const fromApproval = (approval: WorkflowInstanceApprovalDto): WorkflowRunHistoryEntry => ({
  key: `approval-${approval.id}`,
  action: DECISION_ACTIONS[approval.decision] ?? approval.decision,
  step: approval.stageName,
  person: approval.approverName?.trim() || null,
  intendedFor: intendedFor(approval),
  at: approval.decidedOn ?? "",
  reason: approval.reason?.trim() || null,
});

const fromTask = (task: WorkflowInstanceTaskDto): WorkflowRunHistoryEntry => ({
  key: `task-${task.id}`,
  action: `Task completed: ${task.title?.trim() || "Task"}`,
  step: task.stageName,
  person: task.completedByName?.trim() || task.assigneeName?.trim() || null,
  intendedFor: intendedFor(task),
  at: task.completedOn ?? "",
  reason: null,
});

/**
 * Every step of a case, oldest first, from what GET instance already returns:
 * - the timeline (System_WorkflowInstanceEvents) for moves, closes, pauses, emails and web services;
 * - each decided approval, because an "all approvers" stage has one decision per person but only
 *   one timeline hop - so the hop's own approved / rejected event is left out where decisions exist;
 * - each completed task, which carries its title (its "taskcompleted" event does not).
 */
export const toWorkflowRunHistory = (detail: WorkflowInstanceDetailDto): WorkflowRunHistoryEntry[] => {
  const decided = detail.approvals.filter((approval) => approval.decidedOn && approval.decision !== "pending");
  const stagesWithDecisions = new Set(decided.map((approval) => approval.stageId));

  const events = detail.timeline.filter((event) => {
    if (event.eventType === "taskcompleted") return false;
    const isDecisionHop = event.eventType === "approved" || event.eventType === "rejected";
    return !(isDecisionHop && event.fromStageId !== null && stagesWithDecisions.has(event.fromStageId));
  });

  // A decision or a finished task is what moves the case, so on an equal stamp it goes before the
  // engine hops it caused; within each group the API's own order (Id) is kept.
  const entries = [
    ...decided.map(fromApproval),
    ...detail.tasks.filter((task) => task.status === "completed" && task.completedOn).map(fromTask),
    ...events.map(fromEvent),
  ];

  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (a.entry.at === b.entry.at ? a.index - b.index : a.entry.at < b.entry.at ? -1 : 1))
    .map(({ entry }) => entry);
};
